import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

export interface IntradayTierSettings {
  schema_version: number;
  version: string;
  tiers: {
    id: number;
    label: string;
    code: string;
    active: boolean;
    effective_active: boolean;
    intraday_enabled?: boolean;
    eod_enabled?: boolean;
    revision: number;
  }[];
}

export class IntradayTiersError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = 'IntradayTiersError';
  }
}

// Keep settings requests local to the dialog and out of the asset store.
async function request(
  method: 'GET' | 'POST',
  data: Record<string, unknown>,
  signal?: AbortSignal,
  mode: 'intraday' | 'eod' = 'intraday',
): Promise<IntradayTierSettings> {
  const response = await apiCaller({
    path: `/admin/${mode}_tiers`,
    apiUrl: API_URL,
    method,
    data,
    signal,
  });
  if (response.status < 200 || response.status >= 300) {
    throw new IntradayTiersError(
      response.result?.detail ||
        `${mode === 'intraday' ? 'Intraday' : 'EoD'} tier settings could not be updated. Please retry.`,
      response.status,
    );
  }
  const result = response.result;
  if (
    result?.schema_version !== 1 ||
    typeof result.version !== 'string' ||
    !Array.isArray(result.tiers)
  ) {
    throw new Error(`This ${mode} tier settings response is not supported.`);
  }
  return result;
}

export const loadIntradayTiers = (signal: AbortSignal) =>
  request('GET', {}, signal);
export const saveIntradayTiers = (version: string, ids: number[]) =>
  request('POST', {
    expected_version: version,
    enabled_tier_ids: ids,
  });

export const loadEodTiers = (signal: AbortSignal) =>
  request('GET', {}, signal, 'eod');
export const saveEodTiers = (version: string, ids: number[]) =>
  request(
    'POST',
    {
      expected_version: version,
      enabled_tier_ids: ids,
    },
    undefined,
    'eod',
  );
